import { IAdminAccess, IUserAdmin, IUserWithMergedAccess } from '@/interfaces/admin/users/IUserAdmin.ts';
import {
    IGroupAdmin, IGroupAdminWithUsers, IGroupNamesAndDomains,
} from '@/interfaces/admin/users/IGroupAdmin.ts';
import { ensureDatesAreDateObjects, ensureReducedAccessDatesAreDateObjects } from '@/helpers/admin/adminHelper.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { IAvailableShortTermPowerCountry } from '@/interfaces/admin/IAvailableShortTermPowerCountry.ts';
import { IAdminUserRequestResponse } from '@/interfaces/admin/IAdminUserRequestResponse.ts';
import { useApi } from '../useApi.ts';

const apiCall = useApi('accessApi');

async function getAllUsers (): Promise<Array<IUserWithMergedAccess>> {
    const endpoint = '/AdminAccess/allUsers';
    const allUsersWithMergedAccess = (await apiCall.get<Array<IUserWithMergedAccess>>({ endpoint })).data;

    allUsersWithMergedAccess.forEach(userWithMergedAccess => {
        userWithMergedAccess.user.access = ensureDatesAreDateObjects(userWithMergedAccess.user.access);
        userWithMergedAccess.mergedAccess = {
            assetDatabase: ensureReducedAccessDatesAreDateObjects(userWithMergedAccess.mergedAccess.assetDatabase),
            longTermPower: ensureReducedAccessDatesAreDateObjects(userWithMergedAccess.mergedAccess.longTermPower),
            shortTermMarkets: ensureReducedAccessDatesAreDateObjects(userWithMergedAccess.mergedAccess.shortTermMarkets),
            insightsAccess: userWithMergedAccess.mergedAccess.insightsAccess,
            isUserActive: userWithMergedAccess.mergedAccess.isUserActive,
        };
    });
    return allUsersWithMergedAccess;
}

async function getUser (id: string): Promise<IUserAdmin> {
    const endpoint = `/AdminAccess/user/${id}`;
    const user = (await apiCall.get<IUserAdmin>({ endpoint })).data;
    user.access = ensureDatesAreDateObjects(user.access);
    return user;
}

async function requestAddUser (addUserRequest: IUserAdmin): Promise<IAdminUserRequestResponse> {
    const endpoint = '/AdminAccess/addUser';
    return (await apiCall.post<IUserAdmin, IAdminUserRequestResponse>({ endpoint, payload: addUserRequest })).data;
}

async function requestUpdateUser (addUserRequest: IUserAdmin): Promise<IAdminUserRequestResponse> {
    const endpoint = '/AdminAccess/user';
    return (await apiCall.put<IUserAdmin, IAdminUserRequestResponse>({ endpoint, payload: addUserRequest })).data;
}

async function createExternalApiCredentials (email: string): Promise<void> {
    const endpoint = '/AdminAccess/user/externalApiCredentials';
    await apiCall.post<{ email: string }, void>({ endpoint, payload: { email } });
}

async function requestDeleteUser (userId: string): Promise<void> {
    const endpoint = `/AdminAccess/user/${userId}`;
    await apiCall.delete<string, string>({ endpoint, payload: userId });
}

async function getUserMergedAccess (id: string): Promise<IAdminAccess> {
    const endpoint = `/AdminAccess/user/mergedAccess/${id}`;
    const access = (await apiCall.get<IAdminAccess>({ endpoint })).data;
    return ensureDatesAreDateObjects(access);
}

async function getAllGroupNames (): Promise<Array<IGroupNamesAndDomains>> {
    const endpoint = '/AdminAccess/groupNames';
    return (await apiCall.get<Array<IGroupNamesAndDomains>>({ endpoint })).data;
}

async function getAllUserGroups (id: string): Promise<Array<IGroupAdmin>> {
    const endpoint = `/AdminAccess/userGroups/${id}`;
    const allGroups = (await apiCall.get<Array<IGroupAdmin>>({ endpoint })).data;
    allGroups.forEach(group => {
        group.access = ensureDatesAreDateObjects(group.access);
    });
    return allGroups;
}

async function getAllGroups (): Promise<Array<IGroupAdmin>> {
    const endpoint = '/AdminAccess/groups';
    const allGroups = (await apiCall.get<Array<IGroupAdmin>>({ endpoint })).data;
    allGroups.forEach(group => {
        group.access = ensureDatesAreDateObjects(group.access);
    });
    return allGroups;
}

async function getGroup (id: string): Promise<IGroupAdmin> {
    const endpoint = `/AdminAccess/group/${id}`;
    const group = (await apiCall.get<IGroupAdmin>({ endpoint })).data;
    group.access = ensureDatesAreDateObjects(group.access);
    return group;
}

async function requestAddGroup (addGroupRequest: IGroupAdminWithUsers): Promise<IAdminUserRequestResponse> {
    const endpoint = '/AdminAccess/addGroup';
    return (await apiCall.post<IGroupAdminWithUsers, IAdminUserRequestResponse>({ endpoint, payload: addGroupRequest })).data;
}

async function requestUpdateGroup (updateGroupRequest: IGroupAdminWithUsers): Promise<IAdminUserRequestResponse> {
    const endpoint = '/AdminAccess/group';
    return (await apiCall.put<IGroupAdminWithUsers, IAdminUserRequestResponse>({ endpoint, payload: updateGroupRequest })).data;
}

async function requestDeleteGroup (groupId: string): Promise<void> {
    const endpoint = `/AdminAccess/group/${groupId}`;
    await apiCall.delete<string, string>({ endpoint, payload: groupId });
}

async function getUsersForGroup (id: string): Promise<Array<IUserAdmin>> {
    const endpoint = `/AdminAccess/usersForGroup/${id}`;
    const allUsers = (await apiCall.get<Array<IUserAdmin>>({ endpoint })).data;
    allUsers.forEach(user => {
        user.access = ensureDatesAreDateObjects(user.access);
    });
    return allUsers;
}

async function getAvailableLongTermPowerCountries (): Promise<Array<IIdName>> {
    const endpoint = '/AccessManagement/GetAvailableLongTermPowerCountries';
    return (await apiCall.get<Array<IIdName>>({ endpoint })).data;
}

async function getAvailableShortTermPowerCountries (): Promise<Array<IAvailableShortTermPowerCountry>> {
    const endpoint = '/AccessManagement/GetAvailableShortTermPowerCountries';
    return (await apiCall.get<Array<IAvailableShortTermPowerCountry>>({ endpoint })).data;
}

async function runAsUser (email: string): Promise<void> {
    const endpoint = '/AdminAccess/RunAsUser';
    await apiCall.post<string, IUserAdmin>({ endpoint, payload: email });
}

async function stopRunningAsDifferentUser (): Promise<void> {
    const endpoint = '/AdminAccess/StopRunningAsDifferentUser';
    await apiCall.get<void>({ endpoint });
}

export const adminApi = {
    getAllUsers,
    requestAddUser,
    requestUpdateUser,
    createExternalApiCredentials,
    getUser,
    requestDeleteUser,
    getGroup,
    getAllUserGroups,
    getAllGroups,
    requestAddGroup,
    requestUpdateGroup,
    requestDeleteGroup,
    getUsersForGroup,
    getUserMergedAccess,
    getAllGroupNames,
    getAvailableShortTermPowerCountries,
    getAvailableLongTermPowerCountries,
    runAsUser,
    stopRunningAsDifferentUser,
};
